/**
 * Turns the newest-first message list the shared contexts keep into a
 * chronological render list with day separators inserted.
 *
 * Separators carry the instant, not a formatted label: the label is localized,
 * and callers memoize this list on `messages` alone, so caching the text here
 * would freeze it at the language that was active when the list was built.
 * `ChatDaySeparator` formats at render time instead.
 */
import type { ExtendedChatMessage } from '@wlb/shared/types/chat';

export type ChatTimelineItem =
  | { type: 'day'; key: string; date: Date }
  | { type: 'message'; key: string; message: ExtendedChatMessage };

function toDate(value: Date | number): Date {
  return typeof value === 'number' ? new Date(value) : value;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.toDateString() === b.toDateString();
}

export function buildChatTimeline(
  messages: ExtendedChatMessage[],
): ChatTimelineItem[] {
  const items: ChatTimelineItem[] = [];
  let lastDate: Date | null = null;

  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    const date = toDate(message.createdAt);
    if (!lastDate || !isSameDay(lastDate, date)) {
      items.push({
        type: 'day',
        key: `day-${date.toDateString()}`,
        date,
      });
      lastDate = date;
    }
    items.push({ type: 'message', key: String(message._id), message });
  }

  return items;
}
